'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import type { AuthError } from 'firebase/auth';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { RHFShadcnTextField } from '@/components/RHF';
import FormProvider from '@/components/RHF/FormProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { frontEndRoutes } from '@/constants/routes';
import { AuthLayout } from '@/containers/layouts/AuthLayout';
import { useBoolean } from '@/hooks/utils';
import LogosGoogleIcon from '@/icons/component/LogosGoogleIcon';
import LogosMicrosoftIcon from '@/icons/component/LogosMicrosoftIcon';
import {
  handleAuthFailureWithLinking,
  signInWithGoogle,
  signInWithMicrosoft,
  signInWithPassword,
} from '@/services/firebaseServices/AuthServices/authentication';
import type { EnvironmentDisplayInfo } from '@/services/helpers';
import {
  getEnvironmentDisplayInfo,
} from '@/services/helpers';
import type { SignInWithPasswordFormFields } from '@/types/auth';
import { extractSpaceFromClaims, getClaimsFromJWTCookie, getFirstAccessibleRouteClient, ssoTenantIDForEmail } from '@/utils/auth-helpers';
import { getTargetEnvironmentClient } from '@/utils/target-detection';
import { createSignInWithPasswordSchema } from '@/validations/auth';

/**
 * Resolves the redirect route after successful authentication.
 * Extracts space from JWT claims and routes to the first accessible page.
 */
function resolvePostAuthRedirect(): string {
  const claims = getClaimsFromJWTCookie();
  if (claims === null) {
    return frontEndRoutes.dashboard.base;
  }

  // Extract first available space from claims
  const spaceId = extractSpaceFromClaims(claims);
  if (spaceId !== null && spaceId !== '') {
    return getFirstAccessibleRouteClient(claims, spaceId);
  }

  // No space available - go to dashboard which will resolve
  return frontEndRoutes.dashboard.base;
}

/**
 * Maps Firebase authentication error codes to user-friendly messages
 */
function getAuthErrorMessage(errorCode: string, translate: (key: string) => string): string {
  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return translate('auth.errors.invalidCredentials');
    case 'auth/invalid-email':
      return translate('auth.errors.invalidEmail');
    case 'auth/user-disabled':
      return translate('auth.errors.userDisabled');
    case 'auth/too-many-requests':
      return translate('auth.errors.tooManyRequests');
    case 'auth/network-request-failed':
      return translate('auth.errors.networkError');
    case 'auth/popup-closed-by-user':
      return translate('auth.errors.popupClosed');
    case 'auth/cancelled-popup-request':
      return translate('auth.errors.popupCancelled');
    case 'auth/account-exists-with-different-credential':
      return translate('auth.errors.accountExistsWithDifferentCredential');
    default:
      return translate('auth.errors.genericError');
  }
}

/**
 * Maps custom error messages (non-Firebase) to user-friendly messages
 */
function getCustomErrorMessage(errorMessage: string, translate: (key: string) => string): string {
  if (errorMessage === 'EMAIL_PASSWORD_SIGN_IN_REQUIRED') {
    return translate('auth.errors.emailPasswordSignInRequired');
  }
  return translate('auth.errors.genericError');
}

type SignInPageProps = {
  reason?: string;
};

export function SignInPage(props: SignInPageProps) {
  const { reason } = props;
  const translate = useTranslations();
  const router = useRouter();
  const [environment, setEnvironment] = useState<EnvironmentDisplayInfo | null>(null);
  const [showEnvBadge, setShowEnvBadge] = useState(false);

  const signInWithPasswordSchemaInstance = createSignInWithPasswordSchema({
    emailRequired: translate('auth.validation.emailRequired'),
    emailInvalid: translate('auth.validation.emailInvalid'),
    passwordRequired: translate('auth.validation.passwordRequired'),
    passwordMinLength: translate('auth.validation.passwordMinLength'),
  });

  const methods = useForm<SignInWithPasswordFormFields>({
    mode: 'onChange',
    resolver: zodResolver(signInWithPasswordSchemaInstance),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { handleSubmit, getValues, setError, setFocus, clearErrors, formState: { isSubmitting } } = methods;
  const [activeOAuthProvider, setActiveOAuthProvider] = useState<'google' | 'microsoft' | null>(null);
  // Microsoft uses a two-step flow (press the button, then enter the work
  // email, then open the popup) to match the my.how.fm reference.
  const {
    value: isMicrosoftStep,
    onTrue: openMicrosoftStep,
    onFalse: closeMicrosoftStep,
  } = useBoolean(false);
  const isRedirectingRef = useRef(false);
  const isOAuthLoading = activeOAuthProvider !== null;
  const isAuthInProgress = isSubmitting === true || isOAuthLoading === true;

  useEffect(() => {
    const targetEnv = getTargetEnvironmentClient();
    const envInfo = getEnvironmentDisplayInfo(targetEnv);
    setEnvironment(envInfo);

    const envName = envInfo.name.toLowerCase();
    setShowEnvBadge(envName.includes('staging') === false && envName.includes('production') === false);
  }, []);

  const onSubmit = async (data: SignInWithPasswordFormFields) => {
    try {
      if (data.password === undefined || data.password === '') {
        throw new Error(translate('auth.validation.passwordRequired'));
      }
      await signInWithPassword(data.email, data.password);

      isRedirectingRef.current = true;
      router.push(resolvePostAuthRedirect());
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';
      const errorMessage = getAuthErrorMessage(errorCode, translate);

      toast({
        title: translate('auth.errors.signInFailed'),
        description: errorMessage,
        variant: 'error' as const,
      });
    }
  };

  const handleGoogleSignInClick = useCallback(async () => {
    setActiveOAuthProvider('google');
    try {
      await signInWithGoogle();

      isRedirectingRef.current = true;
      router.push(resolvePostAuthRedirect());
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';
      const errorMessage = getAuthErrorMessage(errorCode, translate);

      toast({
        title: translate('auth.errors.signInFailed'),
        description: errorMessage,
        variant: 'error' as const,
      });

      if (isRedirectingRef.current === false) {
        setActiveOAuthProvider(null);
      }
    }
  }, [router, translate]);

  const handleMicrosoftButtonClick = useCallback(() => {
    // Step 1: pressing Microsoft only asks for the email (matching the
    // my.how.fm flow); the popup opens later, once a tenant-eligible email is
    // entered and "Continue" is pressed.
    openMicrosoftStep();
    setFocus('email');
  }, [openMicrosoftStep, setFocus]);

  const handleMicrosoftBack = useCallback(() => {
    clearErrors('email');
    closeMicrosoftStep();
  }, [clearErrors, closeMicrosoftStep]);

  const handleMicrosoftContinue = useCallback(async () => {
    // Step 2: the Azure app is single-tenant, so Microsoft sign-in only works
    // when we can resolve a tenant from the email. Require a tenant-eligible
    // email before opening the popup (which would otherwise fail with
    // AADSTS700016): block when no email is entered, and when the email's
    // domain has no configured tenant.
    const emailInput = getValues('email').trim();
    if (emailInput === '') {
      setError('email', {
        type: 'manual',
        message: translate('auth.validation.emailRequired'),
      });
      setFocus('email');
      return;
    }
    if (ssoTenantIDForEmail(emailInput) === undefined) {
      setError('email', {
        type: 'manual',
        message: translate('auth.validation.microsoftTenantNotFound'),
      });
      setFocus('email');
      return;
    }

    setActiveOAuthProvider('microsoft');
    try {
      await signInWithMicrosoft(emailInput);

      isRedirectingRef.current = true;
      router.push(resolvePostAuthRedirect());
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code ?? '';

      // Handle account linking for Microsoft sign-in
      if (errorCode === 'auth/account-exists-with-different-credential') {
        try {
          const linkedResult = await handleAuthFailureWithLinking(error as AuthError);

          if (linkedResult !== null) {
            toast({
              title: translate('auth.success.accountLinked'),
              description: translate('auth.success.accountLinkedDescription'),
              variant: 'primary' as const,
            });

            isRedirectingRef.current = true;
            router.push(resolvePostAuthRedirect());
            return;
          }
        } catch (linkError: unknown) {
          const linkErrorMessage = (linkError as { message?: string })?.message ?? '';

          if (linkErrorMessage === 'EMAIL_PASSWORD_SIGN_IN_REQUIRED') {
            toast({
              title: translate('auth.errors.accountLinkingFailed'),
              description: getCustomErrorMessage(linkErrorMessage, translate),
              variant: 'error' as const,
            });
            setActiveOAuthProvider(null);
            return;
          }

          const linkErrorCode = (linkError as { code?: string })?.code ?? '';
          const errorMessage = getAuthErrorMessage(linkErrorCode, translate);

          toast({
            title: translate('auth.errors.accountLinkingFailed'),
            description: errorMessage,
            variant: 'error' as const,
          });
          setActiveOAuthProvider(null);
          return;
        }
      }

      const errorMessage = getAuthErrorMessage(errorCode, translate);

      toast({
        title: translate('auth.errors.signInFailed'),
        description: errorMessage,
        variant: 'error' as const,
      });

      if (isRedirectingRef.current === false) {
        setActiveOAuthProvider(null);
      }
    }
  }, [getValues, setError, setFocus, router, translate]);

  return (
    <AuthLayout>
      {reason === 'no-space-access' && (
        <Alert variant="warning">
          <AlertDescription>
            {translate('auth.signIn.noSpaceAccess')}
          </AlertDescription>
        </Alert>
      )}
      <FormProvider methods={methods}>
        {isMicrosoftStep === false
          ? (
              <div className="space-y-4">
                <div className="grid gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full"
                    onClick={handleGoogleSignInClick}
                    disabled={isAuthInProgress === true}
                    loading={activeOAuthProvider === 'google'}
                  >
                    <LogosGoogleIcon className="mr-2 h-4 w-4" />
                    {translate('auth.signIn.continueWithGoogle')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full"
                    onClick={handleMicrosoftButtonClick}
                    disabled={isAuthInProgress === true}
                    loading={activeOAuthProvider === 'microsoft'}
                  >
                    <LogosMicrosoftIcon className="mr-2 h-4 w-4" />
                    {translate('auth.signIn.continueWithMicrosoft')}
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {translate('auth.signIn.orContinueWith')}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <RHFShadcnTextField
                    name="email"
                    title={translate('auth.signIn.emailLabel')}
                    fieldType="email"
                    startIcon="material-symbols:mail"
                    disabled={isAuthInProgress === true}
                  />

                  <RHFShadcnTextField
                    name="password"
                    title={translate('auth.signIn.passwordLabel')}
                    fieldType="password"
                    startIcon="material-symbols:lock"
                    disabled={isAuthInProgress === true}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    loading={isSubmitting === true}
                    disabled={isAuthInProgress === true}
                  >
                    {translate('auth.signIn.signInButton')}
                  </Button>
                </form>
              </div>
            )
          : (
              <div className="space-y-4">
                <div className="space-y-1 text-center">
                  <h2 className="text-lg font-semibold">
                    {translate('auth.signIn.microsoftStepTitle')}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {translate('auth.signIn.microsoftStepDescription')}
                  </p>
                </div>

                <RHFShadcnTextField
                  name="email"
                  title={translate('auth.signIn.emailLabel')}
                  fieldType="email"
                  startIcon="material-symbols:mail"
                  disabled={isAuthInProgress === true}
                />

                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={handleMicrosoftContinue}
                  disabled={isAuthInProgress === true}
                  loading={activeOAuthProvider === 'microsoft'}
                >
                  <LogosMicrosoftIcon className="mr-2 h-4 w-4" />
                  {translate('auth.signIn.continueWithMicrosoft')}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  className="w-full"
                  onClick={handleMicrosoftBack}
                  disabled={isAuthInProgress === true}
                >
                  {translate('auth.signIn.back')}
                </Button>
              </div>
            )}
      </FormProvider>

      <div className="text-center space-y-2">
        <p className="text-xs text-muted-foreground">
          {translate('auth.signIn.termsText')}
        </p>
        <Link
          href={frontEndRoutes.auth.forgotPassword}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors block"
        >
          {translate('auth.signIn.forgotPassword')}
        </Link>
      </div>

      {showEnvBadge === true && environment !== null && (
        <div className="flex justify-center pt-4">
          <Badge variant="outline" className="text-xs font-normal">
            {environment.name}
          </Badge>
        </div>
      )}
    </AuthLayout>
  );
}
